import { convertToLocale } from "@/utils/money";
import type { StoreOrderHistoryOrder } from "@repo/api-sdk";

/**
 * The **order-insights** module: the single owner of how the storefront
 * *classifies* and *summarizes* a customer's orders. The two things that used to
 * be hand-rolled inside the order-history `queryFn`s - the fulfillment-status
 * predicates and the header/hero aggregations - live here as pure functions, so
 * they are tested as input -> output (no SDK mock) and can't drift.
 *
 * The classification reads from one canonical source, {@link FULFILLMENT_STATES},
 * which the order-history display layer (`order-history-utils`) also consumes to
 * build its labelled tabs - so the hero stats and the order tabs read the same
 * state groups. One deliberate exception: a cancelled order whose supplier made
 * real progress (the api checks delivered, shipped and packed before cancelled)
 * keeps that `fulfillment_status`, so its tab and badge can be Delivered, In
 * transit or Preparing while the stats (`isCancelled`) count it as cancelled.
 * (A Shopify cancel or restock reads Cancelled instead, and for a Shopify order
 * all-cancelled native fulfillments show the Shopify status; see CONTEXT.md.)
 */

// ============================================================
// Canonical fulfillment-state groupings (the single source)
// ============================================================

/** The coarse buyer-facing groups an order's `fulfillment_status` falls into. */
export type FulfillmentGroup =
  | "processing"
  | "preparing"
  | "in_transit"
  | "delivered"
  | "cancelled";

/**
 * The one place that maps a buyer-facing group to the canonical
 * `fulfillment_status` values it owns. Both the order-history tabs
 * (`FULFILLMENT_BUCKETS`) and the stats predicates below read from this, so the
 * two surfaces read the same state groups (a cancelled order with real supplier
 * progress is the one deliberate exception, see the module header). Every state
 * appears in exactly one group.
 */
export const FULFILLMENT_STATES: Record<FulfillmentGroup, readonly string[]> = {
  processing: ["not_fulfilled"],
  preparing: ["fulfilled", "partially_fulfilled"],
  in_transit: ["shipped", "partially_shipped"],
  delivered: ["delivered", "partially_delivered"],
  cancelled: ["canceled"]
};

// ============================================================
// Predicates (buyer-facing, fulfillment-based - matches the tabs)
// ============================================================

/** The order's `status` + `fulfillment_status` - all a predicate needs. */
type OrderStatus = Pick<
  StoreOrderHistoryOrder,
  "status" | "fulfillment_status"
>;

/**
 * Cancelled by either axis: the core order `status`, or the computed
 * `fulfillment_status`. Deliberately the broader of the two so a cancelled order
 * never slips into the spend totals, whichever axis the backend set.
 */
export function isCancelled(order: OrderStatus): boolean {
  return (
    order.status === "canceled" ||
    FULFILLMENT_STATES.cancelled.includes(order.fulfillment_status ?? "")
  );
}

/** In transit: shipped or partially shipped (the `in_transit` tab's states). */
export function isInTransit(order: OrderStatus): boolean {
  return FULFILLMENT_STATES.in_transit.includes(order.fulfillment_status ?? "");
}

/**
 * Delivered: the `delivered` tab's states (`delivered` / `partially_delivered`).
 * Purely fulfillment-based, so the hero "completed" count agrees with the
 * Delivered tab for orders that are not cancelled - a `partially_delivered`
 * order counts as completed here too. (A cancelled order with delivered progress
 * is in the Delivered tab but counted as cancelled, never as completed.)
 *
 * The core order `status: "completed"` is deliberately NOT consulted. The api
 * sets it as soon as payment is captured and every unit has shipped (the
 * `shipmentCreated` hook, or a vendor's manual "complete"), not when the goods
 * arrive; only a vendor's mark-as-delivered (or a Shopify delivered overlay)
 * sets `delivered`. So a `completed` + `shipped` order is still on its way to the
 * salon: it reads In transit, counts as Active and not as Completed (KAN-407).
 */
export function isDelivered(order: OrderStatus): boolean {
  return FULFILLMENT_STATES.delivered.includes(order.fulfillment_status ?? "");
}

/**
 * Active: the salon is still waiting on the order - not cancelled (`isCancelled`)
 * and not delivered (`isDelivered`, which includes `partially_delivered`). The
 * core `status: "completed"` plays no part. The one definition behind the home
 * "Active Orders" tile, the `/orders` "Active Orders" card and `deriveOrderStats`,
 * so they cannot differ. An order is in exactly one of cancelled / completed
 * (delivered) / active.
 */
export function isActive(order: OrderStatus): boolean {
  return !isCancelled(order) && !isDelivered(order);
}

/** A usable amount: a finite number above zero, else `null` ("no answer"). */
function positiveAmount(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/**
 * The total to show for an order.
 *
 * A live order shows `total`. A cancelled order can have `total` 0: when the
 * cancel's refund is booked as a credit line (SS-26622997 and 8 others on Dev),
 * Medusa zeroes `total`, though a cancel alone does not (SS-26814207 keeps its
 * `total`). So a cancelled order shows what the salon actually paid, then falls
 * back:
 *  1. `summary.paid_total` - the gross captured money (refunds are counted
 *     separately, not netted off), so it is the amount that was ordered and paid;
 *  2. `total`;
 *  3. `summary.original_order_total` - only a last resort: Medusa snapshots it
 *     from `order.total` each time it recalculates the order, so after an earlier
 *     partial refund it can be lower than the true original.
 * Values that are not finite or are zero count as "no answer" and are skipped.
 *
 * Keep in step with the api copy of this rule, `displayOrderTotal` in
 * `packages/modules/order-assistant/src/lib/backend.ts` (api repo), which feeds
 * the order assistant: change one and you must change the other.
 */
export function getOrderDisplayTotal(
  order: OrderStatus &
    Pick<StoreOrderHistoryOrder, "total"> & {
      summary?: {
        paid_total?: unknown;
        original_order_total?: unknown;
      } | null;
    }
): number {
  if (!isCancelled(order)) return order.total ?? 0;
  return (
    positiveAmount(order.summary?.paid_total) ??
    positiveAmount(order.total) ??
    positiveAmount(order.summary?.original_order_total) ??
    0
  );
}

// ============================================================
// Aggregations (the header cards + the home hero widgets)
// ============================================================

/** Aggregate stats for the order-history header cards + the home hero. */
export interface CustomerOrderStats {
  /** Orders still awaited: neither cancelled nor delivered (see `isActive`). */
  active: number;
  /**
   * Orders delivered or partially delivered (see `isDelivered`). Not the core
   * `status: "completed"`, which only means paid and fully shipped.
   */
  completed: number;
  /** Exact total number of orders (from the endpoint `count`, all pages). */
  total: number;
  /** Sum of order totals, excluding cancelled orders. */
  totalSpent: number;
  /**
   * Sum of order-line discounts (`discount_total`) across non-cancelled orders -
   * the real "saved through SalonStocker" figure. Drives the home hero Savings
   * card (storefront#76). `0` when no order carried a discount.
   */
  totalSaved: number;
  /** Number of orders currently in transit (shipped / partially shipped). */
  inTransitCount: number;
  /**
   * `created_at` of the most recent in-transit order, or `null` when nothing is
   * in transit. We surface the order's placed-date rather than a delivery ETA -
   * we have no shipping ETA and never fabricate one (storefront#76).
   */
  nextInTransitAt: string | null;
  /** Currency of `totalSpent` / `totalSaved` (from the most recent order). */
  currencyCode: string;
  /**
   * True when the customer has more orders than the sampled window, so the
   * derived counts/spend are a lower bound. `total` is always exact.
   */
  approximate: boolean;
}

/**
 * Derive the header/hero stats from a sampled window of orders (newest-first)
 * plus the exact `total` count. Pure: the `queryFn` fetches, this classifies.
 * Cancelled orders are excluded from spend and the active/completed split; the
 * split is fulfillment-based, so it matches the order tabs for every order that
 * is not cancelled.
 */
export function deriveOrderStats(
  orders: StoreOrderHistoryOrder[],
  total: number
): CustomerOrderStats {
  let active = 0;
  let completed = 0;
  let totalSpent = 0;
  let totalSaved = 0;
  let inTransitCount = 0;
  // Orders arrive newest-first, so the first in-transit one we see is the most
  // recent.
  let nextInTransitAt: string | null = null;
  for (const order of orders) {
    if (isCancelled(order)) continue;
    totalSpent += order.total ?? 0;
    totalSaved += order.discount_total ?? 0;
    if (isInTransit(order)) {
      inTransitCount += 1;
      if (!nextInTransitAt) {
        nextInTransitAt = new Date(order.created_at).toISOString();
      }
    }
    if (isActive(order)) {
      active += 1;
    } else {
      completed += 1;
    }
  }
  return {
    active,
    completed,
    total,
    totalSpent,
    totalSaved,
    inTransitCount,
    nextInTransitAt,
    currencyCode: orders[0]?.currency_code ?? "AUD",
    approximate: orders.length < total
  };
}

/**
 * The salon's total savings as display text, in the orders' currency - the one
 * formatting of that figure, shared by the dashboard's Savings tile and the
 * header chip so the two cannot disagree. `undefined` while the stats are
 * unknown (loading or failed), so each surface decides its own placeholder.
 */
export function formatSavings(
  stats: Pick<CustomerOrderStats, "totalSaved" | "currencyCode"> | undefined
): string | undefined {
  if (!stats) return undefined;
  return convertToLocale({
    amount: stats.totalSaved,
    currency_code: stats.currencyCode
  });
}

/** The supplier a customer has ordered from most, with counts + last order. */
export interface TopSupplier {
  /** Seller id - resolved server-side via the seller-order link. */
  sellerId: string;
  /** Seller name (from the order; prefer the sellers map for display). */
  name: string;
  /** Number of (non-cancelled) orders placed with this supplier. */
  orderCount: number;
  /** `created_at` of the most recent order with this supplier. */
  lastOrderAt: string;
  /**
   * True when the customer has more orders than the sampled window, so the
   * "most ordered" ranking is over recent orders only (a lower bound).
   */
  approximate: boolean;
}

/**
 * The most-ordered supplier over a sampled window (newest-first) plus the exact
 * `total` count. Cancelled orders are excluded; returns `null` for a customer
 * with no qualifying orders. Ranking is over the window, flagged `approximate`
 * when the window is smaller than `total` so we never silently under-count.
 */
export function deriveTopSupplier(
  orders: StoreOrderHistoryOrder[],
  total: number
): TopSupplier | null {
  // Orders arrive newest-first, so the first order seen for a seller is their
  // most recent one.
  const byId = new Map<
    string,
    { name: string; count: number; lastOrderAt: string }
  >();
  for (const order of orders) {
    if (isCancelled(order)) continue;
    const seller = order.seller;
    if (!seller?.id) continue;
    const existing = byId.get(seller.id);
    if (existing) {
      existing.count += 1;
    } else {
      byId.set(seller.id, {
        name: seller.name,
        count: 1,
        lastOrderAt: new Date(order.created_at).toISOString()
      });
    }
  }

  let top: TopSupplier | null = null;
  for (const [sellerId, agg] of byId) {
    if (!top || agg.count > top.orderCount) {
      top = {
        sellerId,
        name: agg.name,
        orderCount: agg.count,
        lastOrderAt: agg.lastOrderAt,
        approximate: false
      };
    }
  }

  if (top) top.approximate = orders.length < total;
  return top;
}
